/**
 * Feb 2026 · User-Directive „Admin-PIN vor jeder Checklisten-Bearbeitung"
 * (1 Sep 2026) — Team-User zeigen den Dialog beim ersten PIN-pflichtigen
 * PATCH einer Buchung.
 *
 * Feb 2026 · 16 Sep 2026 · User-Directive „PIN-Abfrage bei Concierge":
 *   „Jeder Nutzer bestätigt Änderungen mit seinem eigenen PIN."
 *
 * Feb 2026 · 16 Sep 2026 · User-Directive „PIN jedes Mal fragen":
 *   „Der Pin muss jedes Mal gefragt werden wenn Änderungen Speichern
 *   geklickt wird."
 *   → Kein Session-Cache mehr. Jeder Save-Klick öffnet den Dialog neu.
 *
 * Der Dialog verifiziert gegen `/api/me/team-pin/verify` (persönliche
 * PIN aus `db.users.team_pin_hash`) — nicht mehr gegen die zentrale Admin-
 * PIN. Wenn der User noch keine PIN hat, wird beim ersten Save-Versuch der
 * Set-PIN-Modus angeworfen: neue PIN + Bestätigen, danach automatisch
 * weiter zum eigentlichen PATCH.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { KeyRound, X, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

export function useAdminChecklistPin({ isAdmin, enabled }) {
  const promptEnabled = enabled !== undefined ? !!enabled : !!isAdmin;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pinInput, setPinInput] = useState("");
  // "confirm" = normale PIN-Eingabe; "setup" = Erst-Einrichtung
  // (zwei-mal PIN + Confirm). "reset" folgt nach „PIN vergessen".
  const [dialogMode, setDialogMode] = useState("confirm");
  const [pinConfirm, setPinConfirm] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [requestingReset, setRequestingReset] = useState(false);
  const resolverRef = useRef(null);
  const inputRef = useRef(null);
  const cachedPinRef = useRef(null);
  const { isImpersonatingConcierge, otherConciergeUnlocked, unlockOtherConcierge } = useAuth() || {};

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 60);
  }, [open]);

  const clearPin = useCallback(() => {
    cachedPinRef.current = null;
  }, []);

  const openPromptWithMode = useCallback(async () => {
    // Backend-Status abfragen, um Setup-Pfad automatisch zu wählen.
    try {
      const { data } = await api.get("/me/team-pin/status");
      setDialogMode(data?.is_set ? "confirm" : "setup");
    } catch {
      setDialogMode("confirm");
    }
    setPinInput("");
    setPinConfirm("");
    setResetOtp("");
    setOpen(true);
  }, []);

  const getPinHeaders = useCallback(async () => {
    if (!promptEnabled) return {};
    // Feb 2026 · 16 Sep 2026 · User-Directive „PIN jedes Mal fragen":
    //   „Der Pin muss jedes Mal gefragt werden wenn Änderungen Speichern
    //   geklickt wird."
    // Kein Cache mehr — jeder Save-Klick (und jede Undo-Aktion) löst
    // eine frische PIN-Prompt aus. `cachedPinRef` bleibt als Struktur
    // erhalten (defensive Cleanup-Aufrufer greifen weiter darauf zu),
    // wird aber NIE mehr für einen Header-Return verwendet.
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      openPromptWithMode();
    });
  }, [promptEnabled, openPromptWithMode]);

  const requestReset = async () => {
    setRequestingReset(true);
    try {
      await api.post("/me/team-pin/request-reset");
      toast.success("Reset-Code an Ihre E-Mail gesendet.");
      setDialogMode("reset");
      setPinInput("");
      setPinConfirm("");
      setResetOtp("");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setRequestingReset(false); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;

    // === Setup: erst POST /me/team-pin, dann verify + cache-fill ===
    if (dialogMode === "setup") {
      if (!/^\d{4}$/.test(pinInput)) { toast.error("Bitte 4 Ziffern eingeben."); return; }
      if (pinInput !== pinConfirm) { toast.error("PINs stimmen nicht überein."); return; }
      setBusy(true);
      try {
        await api.post("/me/team-pin", { new_pin: pinInput });
        cachedPinRef.current = pinInput;
        toast.success("PIN gespeichert.");
        setOpen(false);
        resolverRef.current?.({ "X-Admin-Pin": pinInput });
        resolverRef.current = null;
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      } finally { setBusy(false); }
      return;
    }

    // === Reset: PUT /me/team-pin mit reset_otp + neue PIN ===
    if (dialogMode === "reset") {
      if (!resetOtp.trim()) { toast.error("Bitte Reset-Code eingeben."); return; }
      if (!/^\d{4}$/.test(pinInput)) { toast.error("Bitte 4 Ziffern eingeben."); return; }
      if (pinInput !== pinConfirm) { toast.error("PINs stimmen nicht überein."); return; }
      setBusy(true);
      try {
        await api.put("/me/team-pin", { reset_otp: resetOtp.trim(), new_pin: pinInput });
        cachedPinRef.current = pinInput;
        toast.success("PIN neu vergeben.");
        setOpen(false);
        resolverRef.current?.({ "X-Admin-Pin": pinInput });
        resolverRef.current = null;
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      } finally { setBusy(false); }
      return;
    }

    // === Default: confirm ===
    if (!/^\d{4}$/.test(pinInput)) { toast.error("Bitte 4 Ziffern eingeben."); return; }
    setBusy(true);
    try {
      await api.post("/me/team-pin/verify", { pin: pinInput });
      cachedPinRef.current = pinInput;
      if (isImpersonatingConcierge && !otherConciergeUnlocked && typeof unlockOtherConcierge === "function") {
        try { unlockOtherConcierge(); } catch { /* best-effort */ }
      }
      setOpen(false);
      setPinInput("");
      resolverRef.current?.({ "X-Admin-Pin": pinInput });
      resolverRef.current = null;
    } catch (err) {
      if (err.response?.status === 403) toast.error("PIN ist falsch.");
      else toast.error(formatErr(err.response?.data?.detail) || err.message);
      setPinInput("");
      setTimeout(() => inputRef.current?.focus(), 20);
    } finally { setBusy(false); }
  };

  const cancel = () => {
    setOpen(false);
    setPinInput("");
    setPinConfirm("");
    setResetOtp("");
    resolverRef.current?.(null);
    resolverRef.current = null;
  };

  const heading =
    dialogMode === "setup" ? "Persönliche PIN einrichten"
    : dialogMode === "reset" ? "PIN zurücksetzen"
    : "Persönliche PIN eingeben";

  // Feb 2026 · 16 Sep 2026 · Directive „PIN-Logik für Admin korrigieren":
  // Wenn der Admin in einer Fremd-Concierge-Ansicht ist, explizit klar-
  // stellen, dass er SEINE EIGENE PIN eingeben soll — nicht die des
  // impersonierten Concierges.
  const impersonationHint = isImpersonatingConcierge
    ? " (Ihre eigene PIN — nicht die PIN des Concierges, dessen Ansicht Sie geöffnet haben.)"
    : "";
  const helper =
    dialogMode === "setup"
      ? "Sie speichern zum ersten Mal eine Buchungsänderung. Vergeben Sie einmalig Ihre persönliche 4-stellige PIN — sie bestätigt jede zukünftige Änderung."
      : dialogMode === "reset"
      ? "Geben Sie den 8-stelligen Code aus Ihrer E-Mail und eine neue 4-stellige PIN ein."
      : `Bitte bestätigen Sie mit Ihrer persönlichen PIN. Der Dialog merkt sich die Eingabe für alle weiteren Änderungen an dieser Buchung.${impersonationHint}`;

  const submitLabel =
    dialogMode === "setup" ? "PIN speichern"
    : dialogMode === "reset" ? "PIN zurücksetzen"
    : "Bestätigen";

  const dialog = open ? createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={cancel}
      data-testid="admin-checklist-pin-dialog"
      data-mode={dialogMode}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-white w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={cancel}
          className="absolute top-3 right-3 text-navy-dark/50 hover:text-navy"
          aria-label="Schließen"
          data-testid="admin-checklist-pin-close"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
        <form onSubmit={submit} className="p-6 md:p-8">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.15), color: BRAND_COLORS.goldDark }}
          >
            <KeyRound className="w-5 h-5" strokeWidth={1.6} />
          </div>
          <h2 className="font-display text-2xl text-navy leading-tight mb-1" data-testid="admin-checklist-pin-heading">
            {heading}
          </h2>
          <p className="text-[13px] text-navy-dark/70 leading-relaxed mb-5">
            {helper}
          </p>

          {dialogMode === "reset" && (
            <div className="mb-4">
              <label className="label-caps block mb-2" style={{ color: BRAND_COLORS.gold }}>Reset-Code aus E-Mail</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                maxLength={8}
                value={resetOtp}
                onChange={(e) => setResetOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                data-testid="admin-checklist-pin-reset-otp"
                placeholder="12345678"
                className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-[15px] tracking-[0.3em] text-center text-navy tabular-nums"
                disabled={busy}
              />
            </div>
          )}

          <label className="label-caps block mb-2" style={{ color: BRAND_COLORS.gold }}>
            {dialogMode === "setup" ? "Neue PIN" : dialogMode === "reset" ? "Neue PIN" : "Ihre PIN"}
          </label>
          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={4}
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
            data-testid="admin-checklist-pin-input"
            className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[18px] tracking-[0.5em] text-center text-navy tabular-nums"
            placeholder="••••"
            disabled={busy}
          />

          {(dialogMode === "setup" || dialogMode === "reset") && (
            <>
              <label className="label-caps block mb-2 mt-4" style={{ color: BRAND_COLORS.gold }}>PIN wiederholen</label>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={4}
                value={pinConfirm}
                onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
                data-testid="admin-checklist-pin-input-confirm"
                className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-[18px] tracking-[0.5em] text-center text-navy tabular-nums"
                placeholder="••••"
                disabled={busy}
              />
            </>
          )}

          {dialogMode === "confirm" && (
            <div className="mt-3 text-right">
              <button
                type="button"
                onClick={requestReset}
                disabled={busy || requestingReset}
                data-testid="admin-checklist-pin-forgot-link"
                className="text-[11.5px] tracking-[0.12em] uppercase text-navy-dark/60 hover:text-gold transition-colors disabled:opacity-50"
              >
                {requestingReset ? "Code wird gesendet…" : "PIN vergessen? Code per E-Mail"}
              </button>
            </div>
          )}

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              data-testid="admin-checklist-pin-cancel"
              className="text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 hover:text-navy px-3 py-2"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || pinInput.length !== 4 || ((dialogMode !== "confirm") && pinConfirm.length !== 4)}
              data-testid="admin-checklist-pin-submit"
              className="inline-flex items-center gap-2 text-ivory px-5 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.8} />}
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  ) : null;

  return { getPinHeaders, clearPin, dialog };
}
